<template>
  <el-dialog
    :title="dialogTitle"
    :visible="visible"
    width="780px"
    top="7vh"
    append-to-body
    custom-class="laser-status-dialog"
    @update:visible="handleVisibleChange"
  >
    <div v-loading="loading" class="laser-detail">
      <el-alert v-if="error" :title="error" type="error" :closable="false" show-icon class="mb-3"></el-alert>

      <template v-if="laser">
        <div class="detail-header">
          <div class="detail-title">
            <div class="detail-name">{{ laser.deviceName || deviceName || '-' }}</div>
            <div class="detail-code">
              {{ laser.deviceCode }} · {{ laser.ip }}:{{ laser.port }}
            </div>
          </div>
          <el-tag :type="statusType(laser.dataStatus)" effect="dark" size="medium">
            {{ statusLabel(laser.dataStatus) }}
          </el-tag>
        </div>

        <div class="metric-grid">
          <div class="metric-card primary">
            <div class="metric-label">最新测距</div>
            <div class="metric-value" :class="{ 'value-warning': isBelowThreshold }">
              {{ formatMeters(laser.lastDistance) }}
            </div>
            <div class="metric-sub">采集帧 {{ formatTime(laser.lastFrameTime) }}</div>
          </div>
          <div class="metric-card">
            <div class="metric-label">多久未更新</div>
            <div class="metric-value">{{ formatAge(laser.dataAgeSeconds) }}</div>
            <div class="metric-sub">近60分钟 {{ laser.recent60Count || 0 }} 条</div>
          </div>
          <div class="metric-card">
            <div class="metric-label">超高判定线</div>
            <div class="metric-value">{{ formatMeters(laser.triggerThresholdMeters) }}</div>
            <div class="metric-sub">无船时正常距离 {{ formatMeters(laser.backgroundMeters) }}</div>
          </div>
        </div>

        <div class="detail-section">
          <div class="section-title">采集与入库</div>
          <div class="status-grid">
            <div class="status-item">
              <span>采集端最新帧</span>
              <b>{{ formatTime(laser.lastFrameTime) }}</b>
              <small v-if="laser.frameAgeSeconds !== null && laser.frameAgeSeconds !== undefined">
                {{ formatAge(laser.frameAgeSeconds) }}前
              </small>
            </div>
            <div class="status-item">
              <span>入库最新数据</span>
              <b>{{ formatTime(laser.lastDataTime) }}</b>
              <small v-if="laser.dataAgeSeconds !== null && laser.dataAgeSeconds !== undefined">
                {{ formatAge(laser.dataAgeSeconds) }}前
              </small>
            </div>
            <div class="status-item">
              <span>采集链路</span>
              <el-tag size="mini" :type="ingestType">{{ ingestLabel }}</el-tag>
            </div>
            <div class="status-item">
              <span>超高判定</span>
              <el-tag size="mini" :type="detectionType">{{ detectionLabel }}</el-tag>
            </div>
          </div>
          <div v-if="ingestHint" class="hint-warning">{{ ingestHint }}</div>
          <div v-if="detectionHint" class="hint-warning">{{ detectionHint }}</div>
        </div>

        <div class="detail-section">
          <div class="section-title">设备参数</div>
          <div class="info-grid">
            <div class="info-item">
              <span>工作方式</span>
              <b>{{ detectModeLabel }}</b>
            </div>
            <div class="info-item">
              <span>无船时正常距离</span>
              <b>{{ formatMeters(laser.backgroundMeters) }}</b>
            </div>
            <div class="info-item">
              <span>允许波动</span>
              <b>{{ laser.bgToleranceM == null ? '-' : laser.bgToleranceM + ' m' }}</b>
            </div>
            <div class="info-item">
              <span>连续判定帧数</span>
              <b>{{ laser.obstructionMinFrames || '-' }}</b>
            </div>
            <div class="info-item">
              <span>近60分钟范围</span>
              <b>{{ recentRange }}</b>
            </div>
            <div class="info-item">
              <span>本条数据超过判定线</span>
              <b>{{ laser.lastOverheightFlag === 1 ? '是' : '否' }}</b>
            </div>
          </div>
        </div>

        <div class="detail-section">
          <div class="section-title">最近一次超高记录</div>
          <div v-if="laser.lastOverheightEvent" class="event-card">
            <div class="event-main">
              <div class="event-value">{{ formatMeters(laser.lastOverheightEvent.eventValue) }}</div>
              <div class="event-meta">
                <div>{{ formatTime(laser.lastOverheightEvent.eventTime) }}</div>
                <div>记录编号 #{{ laser.lastOverheightEvent.eventId }}</div>
                <div>是否生成预警：<b :class="{ 'text-warning': !laser.lastOverheightEvent.alertCreated }">{{ eventAlertText }}</b></div>
              </div>
            </div>
            <div v-if="vesselText" class="event-vessel">关联船：{{ vesselText }}</div>
            <div v-if="!laser.lastOverheightEvent.alertCreated" class="hint-warning event-warning">
              {{ eventWarningText }}
            </div>
          </div>
          <div v-else class="event-empty">暂无超高记录</div>
        </div>

        <div class="detail-note">
          怎么看：如果“采集端最新帧”很新、但“入库最新数据”很久，说明接入正常、采样或入库有问题；
          如果两者都很久，说明设备、串口或公网链路断了。是否生成预警还取决于是否匹配到 AIS 船舶。
        </div>
      </template>

      <div v-else-if="!loading && !error" class="detail-empty">未查询到该激光设备</div>
    </div>

    <span slot="footer">
      <el-button @click="handleVisibleChange(false)">关闭</el-button>
      <el-button type="primary" :loading="loading" @click="fetchStatus">重新查询</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { getLaserStatus } from '@/api/laser-status'

export default {
  name: 'LaserStatusDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    bridgeId: {
      type: [Number, String],
      default: null
    },
    deviceId: {
      type: [Number, String],
      default: null
    },
    deviceName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: false,
      error: '',
      laser: null,
      bridgeInfo: {},
      queryTime: null
    }
  },
  computed: {
    dialogTitle() {
      const name = (this.laser && this.laser.deviceName) || this.deviceName
      return name ? `激光状态 - ${name}` : '激光状态'
    },
    isBelowThreshold() {
      if (!this.laser) return false
      const distance = this.laser.lastDistance
      const threshold = this.laser.triggerThresholdMeters
      return distance !== null && distance !== undefined &&
        threshold !== null && threshold !== undefined &&
        Number(distance) < Number(threshold)
    },
    recentRange() {
      if (!this.laser || !this.laser.recent60Count) return '-'
      const min = this.laser.recent60MinDistance
      const max = this.laser.recent60MaxDistance
      if (min === null || min === undefined || max === null || max === undefined) return '-'
      return `${this.formatMeters(min)} – ${this.formatMeters(max)}`
    },
    vesselText() {
      const event = this.laser && this.laser.lastOverheightEvent
      if (!event) return ''
      if (event.vesselName && event.vesselMmsi) {
        return `${event.vesselName}（${event.vesselMmsi}）`
      }
      return event.vesselName || event.vesselMmsi || ''
    },
    ingestLabel() {
      const status = this.laser && this.laser.ingestStatus
      if (status === 'INGEST_OK') return '采集正常'
      if (status === 'SAMPLE_LAG') return '采集正常，但入库滞后'
      if (status === 'INGEST_STALE') return '采集帧延迟'
      if (status === 'NO_FRAME') return '采集端无新帧'
      return '未知'
    },
    ingestType() {
      const status = this.laser && this.laser.ingestStatus
      if (status === 'INGEST_OK') return 'success'
      if (status === 'SAMPLE_LAG' || status === 'INGEST_STALE') return 'warning'
      return 'danger'
    },
    ingestHint() {
      const status = this.laser && this.laser.ingestStatus
      if (status === 'SAMPLE_LAG') return '采集端有新数据，但入库采样慢，需检查采样/入库逻辑。'
      if (status === 'INGEST_STALE') return '采集帧已经不新，检查设备输出和网络稳定性。'
      if (status === 'NO_FRAME') return '采集端没有新帧，优先检查设备、串口和公网链路。'
      return ''
    },
    detectionLabel() {
      const status = this.laser && this.laser.detectionStatus
      if (status === 'BELOW_THRESHOLD') return '未达到超高判定线'
      if (status === 'ALERTED') return '已低于判定线，已生成预警'
      if (status === 'MATCHED_NO_ALERT') return '已低于判定线，已匹配到船舶但预警未生成'
      if (status === 'EVENT_ONLY') return '已低于判定线，仅记录事件（未匹配到 AIS 船舶）'
      if (status === 'DETECTED_NOT_ALERTED') return '已低于判定线，但近期没有预警'
      return '暂无足够数据'
    },
    detectionType() {
      const status = this.laser && this.laser.detectionStatus
      if (status === 'BELOW_THRESHOLD') return 'info'
      if (status === 'ALERTED') return 'success'
      if (status === 'MATCHED_NO_ALERT') return 'danger'
      if (status === 'EVENT_ONLY') return 'warning'
      if (status === 'DETECTED_NOT_ALERTED') return 'danger'
      return 'info'
    },
    detectionHint() {
      const status = this.laser && this.laser.detectionStatus
      if (status === 'MATCHED_NO_ALERT') return '测距已低于判定线，也匹配到了 AIS 船舶，但预警记录未生成，请检查告警写入/推送链路。'
      if (status === 'EVENT_ONLY') return '测距已低于判定线，但没匹配到 AIS 船舶，所以只记录事件、不弹预警。'
      if (status === 'DETECTED_NOT_ALERTED') return '测距已低于判定线，但近期没有预警，需检查匹配/去重/检测逻辑。'
      if (status === 'ALERTED') return '测距低于判定线，且已生成预警。'
      return ''
    },
    detectModeLabel() {
      return this.laser && this.laser.detectMode === 'B' ? '角度扫描' : '水平单光束'
    },
    eventAlertText() {
      const event = this.laser && this.laser.lastOverheightEvent
      if (!event) return ''
      if (event.alertCreated) return '是'
      return event.vesselMmsi ? '否（已匹配船，预警未生成）' : '否（未匹配到 AIS 船舶）'
    },
    eventWarningText() {
      const event = this.laser && this.laser.lastOverheightEvent
      if (!event) return ''
      return event.vesselMmsi
        ? '系统已记录超高并匹配到 AIS 船舶，但预警记录未生成，请检查告警写入/推送链路。'
        : '系统已记录超高，但没有匹配到 AIS 船舶，所以没有进入“预警信息”，也没有推送通知。'
    }
  },
  watch: {
    visible(value) {
      if (value) {
        this.fetchStatus()
      }
    }
  },
  methods: {
    handleVisibleChange(value) {
      this.$emit('update:visible', value)
    },
    async fetchStatus() {
      if (!this.bridgeId || !this.deviceId) {
        this.error = '缺少桥梁或激光设备信息'
        return
      }
      this.loading = true
      this.error = ''
      try {
        const response = await getLaserStatus(this.bridgeId, this.deviceId)
        if (response && response.code === 200 && response.data) {
          const list = response.data.lasers || []
          this.laser = list.length > 0 ? list[0] : null
          this.bridgeInfo = response.data
          this.queryTime = response.data.queryTime
          if (!this.laser) {
            this.error = '未查询到该激光设备'
          }
        } else {
          this.laser = null
          this.error = (response && response.message) || '查询激光状态失败'
        }
      } catch (e) {
        this.laser = null
        this.error = (e && e.message) || '查询激光状态失败，请稍后重试'
      } finally {
        this.loading = false
      }
    },
    statusType(status) {
      if (status === 'NORMAL') return 'success'
      if (status === 'STALE') return 'warning'
      if (status === 'OFFLINE') return 'danger'
      return 'info'
    },
    statusLabel(status) {
      if (status === 'NORMAL') return '运行正常'
      if (status === 'STALE') return '数据延迟'
      if (status === 'OFFLINE') return '长时间无数据'
      if (status === 'NO_DATA') return '暂无数据'
      return '未知'
    },
    formatTime(value) {
      if (!value) return '-'
      if (Array.isArray(value)) {
        const pad = (n) => String(n).padStart(2, '0')
        const [year, month, day, hour, minute, second] = value
        return `${year}-${pad(month)}-${pad(day)} ${pad(hour)}:${pad(minute)}:${pad(second)}`
      }
      let text = String(value).replace('T', ' ')
      const dotIndex = text.indexOf('.')
      if (dotIndex > 0) {
        text = text.substring(0, dotIndex)
      }
      return text
    },
    formatAge(seconds) {
      if (seconds === null || seconds === undefined) return '-'
      if (seconds < 60) return `${seconds} 秒`
      if (seconds < 3600) return `${Math.floor(seconds / 60)} 分钟`
      return `${(seconds / 3600).toFixed(1)} 小时`
    },
    formatMeters(value) {
      if (value === null || value === undefined || value === '') return '-'
      return `${Number(value).toFixed(1)} m`
    }
  }
}
</script>

<style scoped>
.laser-detail {
  min-height: 220px;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.detail-name {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
}

.detail-code {
  margin-top: 4px;
  font-size: 12px;
  color: #9ca3af;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.metric-card {
  padding: 14px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
}

.metric-card.primary {
  background: #eff6ff;
  border-color: #bfdbfe;
}

.metric-label {
  font-size: 12px;
  color: #6b7280;
}

.metric-value {
  margin-top: 6px;
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
}

.metric-value.value-warning {
  color: #f59e0b;
}

.metric-sub {
  margin-top: 4px;
  font-size: 12px;
  color: #9ca3af;
}

.detail-section {
  margin-bottom: 16px;
}

.section-title {
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 16px;
}

.status-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #f9fafb;
  border-radius: 6px;
  font-size: 13px;
}

.status-item span {
  color: #6b7280;
}

.status-item b {
  color: #1f2937;
  font-weight: 600;
}

.status-item small {
  color: #9ca3af;
  margin-left: 8px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px 16px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  padding: 8px 10px;
  background: #f9fafb;
  border-radius: 6px;
  font-size: 13px;
}

.info-item span {
  color: #6b7280;
}

.info-item b {
  color: #1f2937;
}

.hint-warning {
  margin-top: 8px;
  padding: 8px 10px;
  font-size: 12px;
  line-height: 1.6;
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
}

.event-card {
  padding: 14px 16px;
  border: 1px solid #fde68a;
  border-radius: 8px;
  background: #fffbeb;
}

.event-main {
  display: flex;
  align-items: center;
  gap: 18px;
}

.event-value {
  font-size: 26px;
  font-weight: 700;
  color: #f59e0b;
}

.event-meta {
  font-size: 13px;
  line-height: 1.7;
  color: #6b7280;
}

.event-vessel {
  margin-top: 8px;
  font-size: 13px;
  color: #92400e;
}

.event-warning {
  margin-top: 10px;
}

.event-empty {
  padding: 16px;
  text-align: center;
  color: #9ca3af;
  background: #f9fafb;
  border-radius: 8px;
}

.detail-note {
  font-size: 12px;
  line-height: 1.6;
  color: #9ca3af;
}

.detail-empty {
  padding: 40px 0;
  text-align: center;
  color: #9ca3af;
}

.text-warning {
  color: #f59e0b;
}
</style>
